import { useAuthStore } from '../hooks/useAuth';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface AuthResponse {
    token: string;
    user: any;
}

export class ApiError extends Error {
    constructor(
        message: string,
        public status?: number,
        public code?: string,
        public details?: any // For more context
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

// ... (existing code)

class ApiClient {
    private getAuthHeader(): HeadersInit {
        if (typeof window !== 'undefined') {
            try {
                const rawPersisted = localStorage.getItem('auth-storage');
                if (rawPersisted) {
                    const parsed = JSON.parse(rawPersisted);
                    if (parsed?.state && (!parsed.state.isAuthenticated || !parsed.state.token)) {
                        const current = useAuthStore.getState();
                        if (current.isAuthenticated || current.token) {
                            useAuthStore.setState({
                                user: null,
                                token: null,
                                isAuthenticated: false,
                                location: null,
                                hasLocationConsent: false,
                            });
                        }
                        return {};
                    }
                }
            } catch {
                // Ignore storage parse error
            }
        }
        const token = useAuthStore.getState().token;
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeader(),
                ...options.headers,
            },
        });

        // STRICT 401 INTERCEPTOR - Auth Death Spiral Prevention
        const isAuthRoute =
            endpoint.includes('/auth/login') ||
            endpoint.includes('/auth/register') ||
            endpoint.includes('/auth/logout') ||
            endpoint.includes('/carriers/login') ||
            endpoint.includes('/carrier/auth/login') ||
            endpoint.includes('/carrier/auth/register') ||
            endpoint.includes('/carrier/auth/logout');
        const { hasHydrated, logout: doLogout } = useAuthStore.getState();
        if (response.status === 401 && !isAuthRoute) {
            // Don't wipe auth if store hasn't hydrated yet — the token may be valid
            // but wasn't loaded from localStorage in time for the request
            if (!hasHydrated) {
                console.warn('[API] 401 received before hydration — skipping logout');
                throw new ApiError('Auth not ready', 401, 'AUTH_NOT_HYDRATED');
            }

            console.error('[API] 401 Unauthorized - Clearing auth and redirecting');

            // Do NOT wipe entire local storage, just the auth state (server already rejected the token)
            void doLogout({ skipServerRevoke: true, silent: true });

            // Smart Redirect based on current path
            const currentPath = window.location.pathname;
            const isCarrierRoute = currentPath.startsWith('/carrier');
            const target = isCarrierRoute ? '/carrier-login' : '/login';

            // Prevent reload loop if already on the target page
            if (currentPath !== target) {
                window.location.assign(target);
            }

            throw new ApiError('Session expired. Redirecting...', 401, 'AUTH_EXPIRED');
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Request failed' }));
            throw new ApiError(
                error.error || `HTTP ${response.status}`,
                response.status,
                error.code,
                error
            );
        }

        return response.json();
    }

    // ABSOLUTE ZERO: Safe array fetcher - returns empty array on error
    async safeArrayRequest<T>(endpoint: string): Promise<T[]> {
        try {
            const result = await this.request<{ data?: T[], [key: string]: any }>(endpoint);
            // Extract array from common response patterns
            const arrayData = result?.data || (result as any)?.appointments || (result as any)?.docks || (result as any)?.warehouses;
            return Array.isArray(arrayData) ? arrayData : [];
        } catch (error) {
            console.error('[API] Safe array request failed:', error);
            return [];
        }
    }

    // ABSOLUTE ZERO: Safe object fetcher - returns null on error
    async safeObjectRequest<T>(endpoint: string): Promise<T | null> {
        try {
            return await this.request<T>(endpoint);
        } catch (error) {
            console.error('[API] Safe object request failed:', error);
            return null;
        }
    }

    // Auth endpoints
    async register(data: any) {
        return this.request('/auth/register', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async login(email: string, password: string) {
        return this.request<AuthResponse>('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
    }

    async carrierRegister(data: any) {
        return this.request<AuthResponse>('/carrier/auth/register', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async carrierRecover(mcNumber: string) {
        return this.request<{ message: string }>('/carrier/auth/recover', {
            method: 'POST',
            body: JSON.stringify({ mcNumber }),
        });
    }

    async carrierLogin(email: string, password: string) {
        return this.request<AuthResponse>('/carrier/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
    }

    async logout() {
        return this.request<{ ok: boolean; message: string }>('/auth/logout', {
            method: 'POST',
        });
    }

    async carrierLogout() {
        return this.request<{ ok: boolean; message: string }>('/carrier/auth/logout', {
            method: 'POST',
        });
    }

    // Public Carrier endpoints
    async validatePublicCarrierPO(data: any) {
        return this.request<any>('/public/carrier/validate-po', {
            method: 'POST', // Explicitly public, no auth header needed strictly, but client sends if avail
            body: JSON.stringify(data),
        });
    }

    async submitPublicCarrierRequest(data: any) {
        return this.request<any>('/public/carrier/submit-request', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async getCarrierAppointments() {
        return this.request<{ appointments: any[] }>('/carrier/appointments');
    }

    async updateCarrierAppointment(id: string, data: {
        driverName?: string;
        truckNumber?: string;
        trailerNumber?: string;
        trailerType?: string;
        estimatedPalletCount?: number;
        carrierNotes?: string;
    }) {
        return this.request(`/carrier/appointments/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async claimCarrierAppointments(data: { claimTokens?: string[]; claimToken?: string; confirmationCode?: string; poNumber?: string }) {
        return this.request<{ success: boolean; claimedCount: number; claimedIds: string[] }>('/carrier/appointments/claim', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async cancelCarrierAppointment(id: string, reason?: string) {
        return this.request<{ success: boolean; appointment: any; message: string }>(`/carrier/appointments/${id}/cancel`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
        });
    }

    async resendMissingInfo(id: string) {
        return this.request(`/appointments/${id}/resend-missing-info`, {
            method: 'POST',
        });
    }

    async invite(data: any) {
        return this.request('/auth/invite', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async getProfile() {
        return this.request<{ user: any }>('/auth/me');
    }

    // Warehouse endpoints
    async getWarehouses() {
        return this.request<{ warehouses: any[] }>('/warehouses');
    }

    async getWarehouse(id: string) {
        return this.request<{ warehouse: any }>(`/warehouses/${id}`);
    }

    async createWarehouse(data: any) {
        return this.request('/warehouses', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateWarehouse(id: string, data: any) {
        return this.request(`/warehouses/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    // Dock endpoints
    async getDocks(warehouseId: string) {
        return this.request<{ docks: any[] }>(`/warehouses/${warehouseId}/docks`);
    }

    async getDocksStatus() {
        return this.request<{ docks: any[] }>('/docks/status');
    }

    async createDock(warehouseId: string, data: any) {
        return this.request(`/warehouses/${warehouseId}/docks`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateDock(id: string, data: any) {
        return this.request(`/docks/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async deleteDock(id: string) {
        return this.request(`/docks/${id}`, { method: 'DELETE' });
    }

    // Appointment endpoints
    async getAppointments() {
        return this.request<{ appointments: any[] }>('/appointments');
    }

    async createAppointment(data: any) {
        return this.request('/appointments', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async checkIn(id: string, data: any = {}) {
        return this.request(`/appointments/${id}/checkin`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Dock Board & Schedule V3
    async getDockBoard() {
        return this.request<{ docks: any[] }>('/docks/board');
    }

    async getDockSchedule(dockId: string, date: string) {
        return this.request<{ dock: any, appointments: any[] }>(`/docks/${dockId}/schedule?date=${date}`);
    }

    async updateAppointmentStatus(id: string, status: string) {
        return this.request(`/appointments/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        });
    }

    // Exception Workflow
    async updateDockException(dockId: string, data: { note?: string, reason?: string, ownerUserId?: string | null }) {
        return this.request(`/docks/${dockId}/exception`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async resolveDockException(dockId: string) {
        return this.request(`/docks/${dockId}/exception/resolve`, {
            method: 'POST',
        });
    }

    async getDockExceptionEvents(dockId: string) {
        return this.request<{ events: any[] }>(`/docks/${dockId}/exception/events`);
    }

    // Users
    async getUsers(warehouseId?: string) {
        const query = warehouseId ? `?warehouseId=${warehouseId}` : '';
        return this.request<{ users: any[] }>(`/users${query}`);
    }

    // Customers
    async getCustomers(warehouseId?: string) {
        const query = warehouseId ? `?warehouseId=${warehouseId}` : '';
        // Assuming there is an endpoint /customers. If not, I might need to create it or use what's available.
        // Based on previous context, /api/customers exists.
        return this.request<{ customers: any[] }>(`/customers${query}`);
    }

    // POs
    async searchWarehousePOs(query: string, customerId?: string, excludeClosed?: boolean) {
        const params = new URLSearchParams();
        if (query) params.append('q', query);
        if (customerId) params.append('customerId', customerId);
        if (excludeClosed) params.append('excludeClosed', 'true');
        return this.request<{ results: any[] }>(`/warehouse-pos/search?${params.toString()}`);
    }

    async searchSalesOrders(query: string, status?: string) {
        const params = new URLSearchParams();
        if (query) params.append('q', query);
        if (status) params.append('status', status);
        // Exclude SOs already linked to active loads
        params.append('excludeActive', 'true');
        return this.request<{ results: any[] }>(`/sales-orders/search?${params.toString()}`);
    }

    // AI / Risk
    async getDelayRiskDetails(appointmentId: string) {
        return this.request<{ details: any }>(`/ai/delay-risk/details/${appointmentId}`);
    }

    async submitAiFeedback(data: { appointmentId: string, rating: number, comment?: string }) {
        return this.request('/ai/feedback', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async sendRiskOutreach(appointmentId: string) {
        return this.request<{ ok: boolean, messageId?: string }>(`/ai/risk-outreach/email/${appointmentId}`, {
            method: 'POST'
        });
    }

    async copilotAsk(prompt: string, warehouseId?: string) {
        return this.request<{ ok: boolean, answer: string, suggestedActions?: { label: string, href: string }[] }>('/ai/copilot', {
            method: 'POST',
            body: JSON.stringify({ prompt, ...(warehouseId ? { warehouseId } : {}) }),
        });
    }

    // Loads (Phase 4)
    async getLoads(params?: { status?: string; type?: string; q?: string }) {
        const query = new URLSearchParams();
        if (params?.status) query.append('status', params.status);
        if (params?.type) query.append('type', params.type);
        if (params?.q) query.append('q', params.q);
        const qs = query.toString();
        return this.request<{ loads: any[], total: number }>(`/loads${qs ? `?${qs}` : ''}`);
    }

    async getLoad(id: string) {
        return this.request<{ load: any, linkedPOs: any[], linkedSalesOrders: any[] }>(`/loads/${id}`);
    }

    async createLoad(data: any) {
        return this.request<{ load: any, loadNumber: string }>('/loads', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateLoad(id: string, data: any) {
        return this.request<{ load: any }>(`/loads/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async deleteLoad(id: string) {
        return this.request(`/loads/${id}`, { method: 'DELETE' });
    }

    async addLoadPOs(loadId: string, poIds: string[]) {
        return this.request<{ success: boolean, added: number, skipped: number }>(`/loads/${loadId}/pos`, {
            method: 'POST',
            body: JSON.stringify({ poIds }),
        });
    }

    async removeLoadPO(loadId: string, poId: string) {
        return this.request(`/loads/${loadId}/pos/${poId}`, { method: 'DELETE' });
    }
}

export const api = new ApiClient();
